import React from 'react';
import { TidyCalSectionProps } from '../../types/score';

const TidyCalSection: React.FC<TidyCalSectionProps> = () => {
  return (
    <section className="max-w-4xl mx-auto mt-12 mb-16 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-fadeSlideUp" data-component-name="TidyCalSection">
      
      {/* Header section avec design épuré */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-8 py-8 border-b border-gray-100">
        <div className="text-center max-w-3xl mx-auto">
          
          {/* Badge discret */}
          <div className="inline-flex items-center justify-center rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-800 mb-6 animate-pulse">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            Consultation gratuite de 30 minutes offerte
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Passons à l'étape suivante ensemble
          </h2>
          
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Vous venez de découvrir où vous en êtes. Maintenant, <strong className="text-gray-900">transformons ces insights en résultats concrets</strong>. 
            Nos experts vous accompagnent pour développer une stratégie digitale sur-mesure.
          </p>
          
          {/* Points de valeur avec design professionnel */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Plan d'action personnalisé</h3>
              <p className="text-sm text-gray-600">Stratégie adaptée à vos objectifs spécifiques</p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Résultats mesurables</h3>
              <p className="text-sm text-gray-600">Premières améliorations sous 30 jours</p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">ROI optimisé</h3>
              <p className="text-sm text-gray-600">Investissement rentabilisé rapidement</p>
            </div>
          </div>
          
          {/* Message d'urgence subtil */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left max-w-2xl mx-auto">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-amber-800">
                  Le bon moment pour agir
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Chaque jour de retard dans votre transformation digitale représente des opportunités perdues. 
                  <br />Agissez maintenant pendant que cette analyse est encore fraîche dans votre esprit.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Section calendrier */}
      <div className="px-8 py-8">
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Réservez votre créneau</h3>
          <p className="text-gray-600">Choisissez le moment qui vous convient le mieux</p>
        </div>
        
        <div className="w-full flex flex-col items-center">
          <iframe 
            src="/tidycal.html" 
            width="100%" 
            height="1100px" 
            frameBorder="0" 
            scrolling="no" 
            title="Consultation gratuite - Transformation digitale"
            style={{ maxWidth: '800px' }}
            className="rounded-lg border border-gray-200"
          ></iframe>
        </div>
      </div>
      
      {/* Footer avec garanties */}
      <div className="bg-gray-50 px-8 py-6 border-t border-gray-100">
        <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Sans engagement
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            100% confidentiel
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Expertise certifiée
          </div>
        </div>
      </div>
    </section>
  );
};

export default TidyCalSection;
